import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { storageConst } from "../../utils/constants";
import MainButton from "../../components/Button";
import { useNavigate } from "react-router-dom";
import SpinnerIcon from "../../assets/svgs/spinner";
import Notification from "../../components/notification";
import { Mail, Lock, EyeOff, Eye } from "lucide-react";
import { auth } from "../../lib/firebase";
import { signInWithEmailAndPassword } from "@firebase/auth";

const schema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const SignInPage = () => {
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [show, setShow] = useState(false);

  const handleNavigation = () => {
    navigate("/signup");
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: { email: string; password: string }) => {
    try {
      setIsLoader(true);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      const user = userCredential.user;
      if (user && user.emailVerified) {
        localStorage.setItem(storageConst.APPTOKEN, user.uid);
        setNotification({ type: "Success", message: "Login Successfully!" });
        setIsLoader(false);
        navigate("/");
      } else {
        setNotification({
          type: "Verification",
          message:
            "Email not verified. Please verify your email before logging in.",
        });
      }
    } catch (error) {
      console.error("Error during login:", error);
      // alert("Failed to login. Please check your credentials.");
      setNotification({
        type: "Invalid",
        message: "Failed to login. Please check your credentials.",
      });
      setIsLoader(false);
    }
  };

  const Message = () => {
    // alert("Button clicked!"); // Add your custom logic here
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setShow(false);
        setNotification({ type: "", message: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);
  return (
    <div
      className=" bg-bg bg-center min-h-screen w-full py-4 flex items-center justify-center"
      style={{
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="bg-bgCard px-8 py-4 rounded-lg flex flex-col gap-y-5 shadow-md w-full max-w-md">
        <div className="flex justify-center"></div>
        <div>
          <p className=" font-semibold mb-2 text-center text-3xl">
            Welcome Back
          </p>
          <p className="text-center font-normal mb-4 text-base text-textSecondary">
            Enter your credentials to access your account.
          </p>
        </div>
        <div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 h-5 w-5 text-text" />
                <input
                  className={`w-full pl-9 pr-2 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full ${
                    errors.email ? "border-primary" : "border-text"
                  }`}
                  placeholder="Enter your email"
                  type="email"
                  required
                  {...register("email")}
                  aria-label="Email"
                />
                {errors.email && (
                  <p className="text-gradientTwo text-sm mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-3.5 h-5 w-5 text-text" />
              <input
                type={showPassword ? "text" : "password"}
                {...register("password")}
                className={`w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full ${
                  errors.password ? "border-error" : "border-text"
                }`}
                placeholder="Password"
              />

              <span
                onClick={togglePasswordVisibility}
                className="absolute cursor-pointer top-4 right-2 flex justify-center items-center text-gray-500"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-text" />
                ) : (
                  <Eye className="h-5 w-5 text-text" />
                )}
              </span>
              {errors.password && (
                <p className="text-gradientTwo text-sm mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            <p className="font-normal cursor-pointer text-sm mt-0 text-primary flex justify-end">
              Forget Password
            </p>
            <div className="">
              <MainButton
                type="submit"
                label={isLoader ? <SpinnerIcon /> : "Log In"}
                onClick={Message}
                className=" mt-5 bg-primary h-[50px] focus:outline-none focus:ring-0 rounded-full justify-center items-center gap-2.5 inline-flex w-full text-base font-medium "
              ></MainButton>
            </div>
          </form>
          {notification.message && (
            <Notification
              type={notification.type}
              label={notification.message}
              onClick={() => setShow(true)}
            />
          )}
        </div>

        <div className="flex gap-x-2 justify-center my-3">
          <p className="text-text text-base font-normal leading-none">
            Don’t have an account?
          </p>
          <p
            className="text-primary cursor-pointer text-base font-medium leading-none"
            onClick={handleNavigation}
          >
            Create an account
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
